<template>
  <div class="nsf">
    <label for="npi-search" class="nsf-label">{{ label }}</label>
    <div class="nsf-box">
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4a5568" stroke-width="2"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <input id="npi-search" v-model="query" autocomplete="off" @input="onInput" />
      <v-progress-circular v-if="searching" indeterminate size="18" width="2" />
    </div>
    <span class="nsf-help"><slot name="help">Search by name or 10-digit NPI. Results come from the national NPI Registry.</slot></span>

    <v-alert v-if="error" type="warning" variant="tonal" density="compact" class="nsf-alert">{{ error }}</v-alert>

    <ul v-if="results.length" role="listbox" aria-label="NPI Registry matches" class="nsf-list">
      <li v-for="r in results" :key="r.npi" role="option" :aria-selected="r.npi === selectedNpi">
        <button type="button" class="nsf-opt" :class="{ on: r.npi === selectedNpi }" @click="choose(r)">
          <span class="nsf-opt-main">
            <strong>{{ r.name }}</strong>
            <span class="nsf-sub">{{ [r.specialty, cityOf(r)].filter(Boolean).join(' · ') }}</span>
          </span>
          <span class="nsf-opt-side">
            <span class="nsf-npi">NPI {{ r.npi }}</span>
            <span v-if="r.npi === selectedNpi" class="nsf-check">&#10003;</span>
          </span>
        </button>
      </li>
    </ul>
    <span v-else-if="searched && !searching && !error" class="nsf-none">{{ noneText }}</span>
  </div>
</template>

<script>
import { searchNpi } from './providersApi'
import { errorMessage } from './providerErrors'

const DEBOUNCE_MS = 300
const MIN_CHARS = 2

export default {
  name: 'NpiSearchField',
  props: {
    initialQuery: { type: String, default: '' },
    reviewEntityId: { type: [Number, String], default: null },
    label: { type: String, default: 'Find the provider' }
  },
  emits: ['pick'],
  data() {
    return { query: this.initialQuery, results: [], selectedNpi: null, searching: false, searched: false, error: '', timer: null, seq: 0 }
  },
  mounted() {
    if (this.query.trim().length >= MIN_CHARS) this.run()
  },
  beforeUnmount() {
    clearTimeout(this.timer)
    this.seq += 1
  },
  computed: {
    noneText() {
      const q = this.query.trim()
      const bareWord = q && !/\s/.test(q) && !/^\d{10}$/.test(q)
      return bareWord ? "Add the provider's first name, or search by NPI." : 'No matches in the NPI Registry.'
    }
  },
  methods: {
    cityOf(r) {
      const l = (r.locations || [])[0]
      return l ? [l.city, l.state].filter(Boolean).join(', ') : ''
    },
    onInput() {
      clearTimeout(this.timer)
      this.seq += 1
      if (this.query.trim().length < MIN_CHARS) {
        this.results = []
        this.dropStalePick()
        this.searching = false
        this.searched = false
        this.error = ''
        return
      }
      this.searching = true
      this.timer = setTimeout(this.run, DEBOUNCE_MS)
    },
    async run() {
      const seq = ++this.seq
      this.searching = true
      this.error = ''
      try {
        const res = await searchNpi(this.query.trim(), this.reviewEntityId)
        if (seq !== this.seq) return
        this.results = res.results || []
        this.searched = true
        this.dropStalePick()
      } catch (e) {
        if (seq !== this.seq) return
        this.results = []
        this.dropStalePick()
        this.error = errorMessage(e, "We couldn't search the NPI Registry. Try again, or add without an NPI.")
      } finally {
        if (seq === this.seq) this.searching = false
      }
    },
    // A new search that no longer lists the pick clears it, and tells the parent.
    dropStalePick() {
      if (this.selectedNpi && !this.results.some(r => r.npi === this.selectedNpi)) {
        this.selectedNpi = null
        this.$emit('pick', null)
      }
    },
    choose(r) {
      this.selectedNpi = r.npi
      this.$emit('pick', r)
    }
  }
}
</script>

<style scoped>
.nsf { display: flex; flex-direction: column; gap: 6px; }
.nsf-label { font-size: 13px; font-weight: 600; }
.nsf-box { display: flex; align-items: center; gap: 8px; height: 48px; border: 2px solid #2169a8; border-radius: 10px; padding: 0 12px; }
.nsf-box input { flex: 1 1 auto; border: none; outline: none; font: inherit; font-size: 15px; color: #0d1e35; background: transparent; }
.nsf-help { font-size: 12px; color: #4a5568; }
.nsf-none { font-size: 13px; color: #4a5568; }
.nsf-list { list-style: none; margin: 4px 0 0; padding: 6px; border: 1px solid #e6e1d4; border-radius: 10px; display: flex; flex-direction: column; gap: 4px; max-height: 260px; overflow-y: auto; }
.nsf-opt { width: 100%; display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 8px; font: inherit; font-size: 14px; color: #0d1e35; cursor: pointer; box-sizing: border-box; background: #fff; border: 1px solid transparent; min-height: 44px; }
.nsf-opt.on { background: #daeefa; border-color: #2169a8; }
.nsf-opt-main { display: flex; flex-direction: column; gap: 2px; text-align: left; min-width: 0; }
.nsf-sub { font-size: 13px; color: #4a5568; }
.nsf-opt-side { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }
.nsf-npi { font-size: 12px; color: #4a5568; }
.nsf-check { width: 22px; height: 22px; border-radius: 50%; background: #2169a8; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 13px; }
</style>
